import { Flex, theme, Typography } from 'antd';
import type { GlobalToken } from 'antd';
import { type ReactNode } from 'react';
import styled from 'styled-components';

import { type PlaybookGradient, resolveBackground } from './gradients';
import { radius } from '../../theme/radius';

export type { PlaybookGradient } from './gradients';

export type PlaybookCardProps = {
  owner?: string;
  title: string;
  subtitle?: string;
  image?: string;
  gradient?: PlaybookGradient | (string & {});
  selected?: boolean;
  /** Fired when the card is clicked/activated (e.g. navigate to the playbook). */
  onClick?: () => void;
  menu?: ReactNode;
  className?: string;
};

export const PlaybookCard = ({
  owner,
  title,
  subtitle,
  image,
  gradient,
  selected = false,
  onClick,
  menu,
  className,
}: PlaybookCardProps) => {
  const { token } = theme.useToken();

  // The card is clickable, but the ⋯ menu lives inside it — ignore events that
  // originate from the menu so opening/using it doesn't also fire the card.
  const fromMenu = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest('[data-playbook-menu]');

  return (
    <StyledCard
      className={className}
      $token={token}
      $selected={selected}
      $clickable={!!onClick}
      onClick={
        onClick
          ? (e) => {
              if (!fromMenu(e.target)) onClick();
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? title : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (
                !fromMenu(e.target) &&
                (e.key === 'Enter' || e.key === ' ')
              ) {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div
        style={{
          minHeight: 150,
          margin: 8,
          borderRadius: radius.box,
          background: image
            ? `url("${image}") center / cover no-repeat`
            : resolveBackground(gradient),
        }}
      />
      <Flex vertical style={{ flex: 1, padding: '8px 18px 0' }}>
        <Flex align="flex-start" justify="space-between" gap={8}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {owner && (
              <Typography.Text
                type="secondary"
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: token.fontSizeSM,
                }}
              >
                {owner}
              </Typography.Text>
            )}
            <Typography.Title
              level={4}
              ellipsis={{ tooltip: title }}
              style={{
                margin: '0 0 10px',
                lineHeight: 1.2,
                letterSpacing: '-0.04em',
              }}
            >
              {title}
            </Typography.Title>
          </div>
          {menu && (
            <div data-playbook-menu style={{ flexShrink: 0 }}>
              {menu}
            </div>
          )}
        </Flex>
        {subtitle && (
          <Typography.Paragraph
            type="secondary"
            ellipsis={{ rows: 2, tooltip: subtitle }}
            style={{ marginBottom: 10 }}
          >
            {subtitle}
          </Typography.Paragraph>
        )}
      </Flex>
    </StyledCard>
  );
};

export default PlaybookCard;

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

const StyledCard = styled.div<
  TokenProps & { $selected: boolean; $clickable: boolean }
>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorBgContainer};
  border: 2px solid
    ${({ $token, $selected }) =>
      $selected ? $token.colorPrimary : 'transparent'};
  box-shadow: ${({ $token }) => $token.boxShadowTertiary};
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: border-color ${({ $token }) => $token.motionDurationMid}
    ${({ $token }) => $token.motionEaseInOut};

  &:hover {
    border-color: ${({ $token, $selected }) =>
      $selected ? $token.colorPrimary : $token.colorBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }
`;

